/* Video engagement tracking.
   One video_play per <video> per page load, plus video_watched when playback
   crosses 90% of duration. The 90% check runs on timeupdate because looping
   gallery videos never fire `ended`. Media events don't bubble, so both
   listeners sit on document in the capture phase. External (YouTube/Vimeo)
   embeds are not covered. */
(() => {
  const capture = (name, props) => {
    if (!window.posthog || typeof window.posthog.capture !== 'function') return;
    window.posthog.capture(name, props);
  };

  const played = new WeakSet();
  const watched = new WeakSet();

  const videoProps = (video) => {
    const src = video.currentSrc || video.src || '';
    return {
      video_src: src.split('/').pop().split('?')[0] || null,
      autoplay: video.autoplay,
      muted: video.muted,
      loop: video.loop,
      duration: Number.isFinite(video.duration) ? Math.round(video.duration) : null,
      media_id: video.closest('[data-media-id]')?.dataset.mediaId || null,
      section: video.closest('.shopify-section')?.id || null,
    };
  };

  document.addEventListener(
    'play',
    (e) => {
      const video = e.target;
      if (!(video instanceof HTMLVideoElement) || played.has(video)) return;
      played.add(video);
      capture('video_play', videoProps(video));
    },
    true
  );

  document.addEventListener(
    'timeupdate',
    (e) => {
      const video = e.target;
      if (!(video instanceof HTMLVideoElement) || watched.has(video)) return;
      if (!Number.isFinite(video.duration) || video.duration === 0) return;
      if (video.currentTime / video.duration < 0.9) return;
      watched.add(video);
      capture('video_watched', videoProps(video));
    },
    true
  );

  /* Reassurance cards on product pages.
     call_us_tap: a tap on the call-us number. While open the whole card is the
     link (a stretched ::after), so tap_target says whether the tap landed on
     the number itself or elsewhere on the card; call_status says whether we
     were open or the tap came after hours.
     guarantee_toggle: the Availability Guarantee panel opened or closed.
     `toggle` doesn't bubble, so it listens in the capture phase. */
  const hitsText = (el, e) =>
    Array.from(el.getClientRects()).some(
      (r) => e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom
    );

  document.addEventListener('click', (e) => {
    const link = e.target instanceof Element ? e.target.closest('a.champkoi-call-us__phone') : null;
    if (!link) return;
    const card = link.closest('.champkoi-call-us');
    capture('call_us_tap', {
      call_status: card && card.classList.contains('champkoi-call-us--closed') ? 'closed' : 'open',
      tap_target: e.detail === 0 ? 'keyboard' : hitsText(link, e) ? 'number' : 'card',
    });
  });

  /* size_unit_change: the shopper flipped koi sizes between cm and in. */
  document.addEventListener('champkoi:prefs-changed', (e) => {
    const unit = e.detail && e.detail.patch && e.detail.patch.size_unit;
    if (unit) capture('size_unit_change', { size_unit: unit });
  });

  document.addEventListener(
    'toggle',
    (e) => {
      const panel = e.target;
      if (!(panel instanceof HTMLDetailsElement) || !panel.classList.contains('champkoi-availability')) return;
      capture('guarantee_toggle', { action: panel.open ? 'open' : 'close' });
    },
    true
  );

  /* koi_grid_cards_seen: how many sold and available koi cards a shopper really
     saw in a collection or search grid, so sold koi can be judged by being seen
     rather than only by being clicked. A card counts once per page load, at half
     visible, even when a filter re-renders the grid. Sent when the page is
     hidden, as a beacon so it survives leaving the page; a later return sends
     only the new cards. */
  const grid = document.getElementById('ProductGridContainer');
  if (grid && 'IntersectionObserver' in window) {
    const seen = new Set();
    let sold = 0;
    let available = 0;

    const cardSeen = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const card = entry.target;
          cardSeen.unobserve(card);
          const key = card.querySelector('a[href*="/products/"]')?.getAttribute('href');
          if (!key || seen.has(key)) return;
          seen.add(key);
          if (card.classList.contains('koi-card--sold')) sold += 1;
          else available += 1;
        });
      },
      { threshold: 0.5 }
    );

    const watchCards = () => grid.querySelectorAll('.card-wrapper--koi').forEach((card) => cardSeen.observe(card));
    watchCards();
    new MutationObserver(watchCards).observe(grid, { childList: true, subtree: true });

    const hideSoldOn = () => {
      const params = new URLSearchParams(window.location.search);
      return params.get('filter.v.availability') === '1' || params.get('filter.p.availability') === '1';
    };

    const send = () => {
      if (!sold && !available) return;
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      window.posthog.capture(
        'koi_grid_cards_seen',
        { sold_seen: sold, available_seen: available, hide_sold: hideSoldOn() },
        { transport: 'sendBeacon', send_instantly: true }
      );
      sold = 0;
      available = 0;
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') send();
    });
    window.addEventListener('pagehide', send);
  }
})();
